import { useMemo } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, Tooltip, ReferenceLine, Legend, } from "recharts";
import { Card, Typography, Empty } from "antd";
import { AimOutlined, ExclamationCircleOutlined, LineChartOutlined, } from "@ant-design/icons";

const { Title, Text } = Typography;

export function WeightedProgressChart({
  projects,
  drillDownLevel,
  selectedProjectId,
  selectedActivityId,
  onProjectClick,
  onActivityClick,
}) {

  const chartData = useMemo(() => {
    if (drillDownLevel === "project") {
      return projects.map((p) => ({
        id: p.oid,
        name: p.codigo,
        fullName: p.codigo + " " + p.nombre,
        value: p.porcentajePonderado,
        type: "project",
      }));
    }

    if (drillDownLevel === "activity" && selectedProjectId) {
      const project = projects.find((p) => p.oid === selectedProjectId);
      if (!project) return [];
      return project.actividades.map((a) => ({
        id: a.id,
        name: a.codigo,
        fullName: a.codigo + " " + a.descripcion,
        value: a.porcentajePonderado,
        type: "activity",
      }));
    }

    if (
      drillDownLevel === "indicator" &&
      selectedProjectId &&
      selectedActivityId
    ) {
      const project = projects.find((p) => p.oid === selectedProjectId);
      const activity = project?.actividades.find(
        (a) => a.id === selectedActivityId
      );
      if (!activity) return [];
      return activity.tareas.flatMap((t) =>
        t.indicadores.map((ind) => ({
          id: ind.id,
          name: `${t.codigo}`,
          fullName: `Tarea: ${t.codigo} - ${t.descripcion}\nIndicador: ${ind.indicador}`, 
          value: ind.porcentaje,
          type: "indicator",
        }))
      );
    }


    return [];
  }, [projects, drillDownLevel, selectedProjectId, selectedActivityId]);

  const getBarColor = (value) => {
    if (value > 100) return "#00E676";
    if (value === 100) return "#009728";
    if (value >= 75) return "#317EDA";
    if (value >= 25) return "#FFA500";
    return "#D72638";
  };


  const handleBarClick = (data) => {
    if (drillDownLevel === "project" && onProjectClick) onProjectClick(data.id);
    if (drillDownLevel === "activity" && onActivityClick) onActivityClick(data.id);
  };


  const getChartTitle = () => {
    if (drillDownLevel === "project") return "Progreso Ponderado por Proyecto";
    if (drillDownLevel === "activity") return "Progreso por Actividad";
    if (drillDownLevel === "indicator") return "Porcentaje por Tarea e Indicador";
    return "Progreso Ponderado";
  };

  const getChartDescription = () => {
    if (drillDownLevel === "project")
      return "Avance ponderado de cada proyecto. Haz clic en un proyecto para ver sus actividades.";
    if (drillDownLevel === "activity")
      return "Avance de cada actividad dentro del proyecto. Haz clic en una actividad para ver sus indicadores.";
    if (drillDownLevel === "indicator")
      return "Porcentaje de cumplimiento de cada indicador (incluye sobrecumplidos).";
    return "";
  };

  // 🔹 Tooltip personalizado
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: 8,
            padding: 10,
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          }}
        >
          <Text strong style={{ whiteSpace: "pre-line", display: "block" }}>
            {data.fullName}
          </Text>

          <div style={{ marginTop: 4 }}>
            <Text style={{ color: getBarColor(data.value), fontWeight: "bold" }}>
              {data.value.toFixed(1)}%
            </Text>
          </div>
          {data.type !== "indicator" && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Clic para ver detalles
            </Text>
          )}
        </div>
      );
    }
    return null;
  };

  // 🔹 Si no hay datos
  if (chartData.length === 0) {
    return (
      <Card
        style={{
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          paddingBottom: 10,
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AimOutlined style={{ color: "#317EDA", fontSize: 20 }} />
            <Title level={5} style={{ margin: 0, color: "#042956" }}>
              {getChartTitle()}
            </Title>
          </div>
        }
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {getChartDescription()}
        </Text>

        <Empty
          image={<ExclamationCircleOutlined style={{ fontSize: 48, color: "#ccc" }} />}
          description={
            <div>
              <Text strong>No hay información disponible</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 13 }}>
                No se encontraron datos para los filtros seleccionados.
              </Text>
            </div>
          }
          style={{ padding: "60px 0" }}
        />
      </Card>
    );
  }

  return (
    <Card
      style={{
        borderRadius: 12,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        paddingBottom: 10,
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LineChartOutlined style={{ color: "#317EDA", fontSize: 20 }} />
          <Title level={5} style={{ margin: 0, color: "#042956" }}>
            {getChartTitle()}
          </Title>
        </div>
      }
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {getChartDescription()}
      </Text>

      <div style={{ width: "100%", height: 600 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 60, left: 0, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <ReferenceLine x={100} stroke="#009728" strokeDasharray="3 3" label="Meta 100%" />
            <ReferenceLine x={75} stroke="#317EDA" strokeDasharray="3 3" label="75%" />
            <ReferenceLine x={25} stroke="#FFA500" strokeDasharray="3 3" label="25%" />
            <XAxis
              type="number"
              domain={[0, "auto"]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12 }}
              width={90}
              tickFormatter={(v) =>
                v.length > 12 ? `${v.substring(0, 12)}...` : v
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              align="center"
              height={36}
              wrapperStyle={{
                fontSize: 12,
                marginTop: 10,
              }}
              payload={[
                { value: "Sobrecumplido", type: "square", color: "#00E676" },
                { value: "Completado", type: "square", color: "#009728" },
                { value: "En Progreso", type: "square", color: "#317EDA" },
                { value: "En Riesgo", type: "square", color: "#FFA500" },
                { value: "Atrasado", type: "square", color: "#D72638" },
              ]}
            />
            <Bar
              name="Porcentaje de avance"
              dataKey="value"
              radius={[0, 4, 4, 0]}
              cursor={drillDownLevel !== "indicator" ? "pointer" : "default"}
              onClick={handleBarClick}
            >
              {chartData.map((entry, index) => (
                <Cell key={index} fill={getBarColor(entry.value)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
